import { getNumberFormatter } from './numbers';

const DEFAULT_FALLBACK = '$0.00';

export interface FormatUsdThreshold {
  fractionDigits: number;
  /**
   * When the absolute value is below this limit, use the configured fraction digits.
   */
  lessThan: number;
}

export interface FormatUsdOptions {
  /**
   * Fraction digits to use when no threshold matches. Defaults to 2.
   */
  defaultFractionDigits?: number;
  /**
   * String to return when the value is null, undefined, or NaN. Defaults to "$0.00".
   */
  fallback?: string;
  /**
   * Enforce a maximum number of fraction digits after threshold resolution.
   */
  maximumFractionDigits?: number;
  /**
   * Enforce a minimum number of fraction digits after threshold resolution.
   */
  minimumFractionDigits?: number;
  /**
   * Ordered list of thresholds to control fraction digits for small magnitudes.
   */
  thresholds?: readonly FormatUsdThreshold[];
  /**
   * Whether to include thousands separators. Defaults to true.
   */
  useGrouping?: boolean;
}

export const USD_SMART_CENTS_THRESHOLDS: readonly FormatUsdThreshold[] = [
  { lessThan: 0.01, fractionDigits: 4 },
  { lessThan: 1, fractionDigits: 3 },
];

const DEFAULT_THRESHOLDS = USD_SMART_CENTS_THRESHOLDS;

const DEFAULT_FRACTION_DIGITS = 2;

function resolveFractionDigits(
  absoluteValue: number,
  thresholds: readonly FormatUsdThreshold[] | undefined,
  defaultFractionDigits: number
): number {
  if (thresholds) {
    for (const threshold of thresholds) {
      if (absoluteValue < threshold.lessThan) {
        return threshold.fractionDigits;
      }
    }
  }

  return defaultFractionDigits;
}

export function formatUsd(value: number | null | undefined, options?: FormatUsdOptions): string {
  const fallback = options?.fallback ?? DEFAULT_FALLBACK;

  if (value === null || value === undefined || Number.isNaN(value)) {
    return fallback;
  }

  const defaultFractionDigits = options?.defaultFractionDigits ?? DEFAULT_FRACTION_DIGITS;
  const thresholds = options?.thresholds ?? DEFAULT_THRESHOLDS;
  const absoluteValue = Math.abs(value);

  let fractionDigits = resolveFractionDigits(absoluteValue, thresholds, defaultFractionDigits);

  if (
    options?.minimumFractionDigits !== undefined &&
    options?.maximumFractionDigits !== undefined &&
    options.minimumFractionDigits > options.maximumFractionDigits
  ) {
    throw new Error('minimumFractionDigits cannot be greater than maximumFractionDigits');
  }

  if (options?.minimumFractionDigits !== undefined) {
    fractionDigits = Math.max(fractionDigits, options.minimumFractionDigits);
  }

  if (options?.maximumFractionDigits !== undefined) {
    fractionDigits = Math.min(fractionDigits, options.maximumFractionDigits);
  }

  return getNumberFormatter('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    useGrouping: options?.useGrouping ?? true,
  }).format(value);
}
